import { useRemoveTicketsFromCategory } from '@pages/event/api/useFetchDeleteTicketCategory';
import { useGetParticipantsByEventId } from '@pages/event/api/useGetParticipantsByEventId';
import AddParticipantToCategoryModal from '@features/add-participant-to-category';
import { SegmentCardCategory } from '@features/segment-card-category';
import { EventSettings } from '@features/api/use-get-settings';
import { Category } from '@pages/event/api/useFetchCategory';
import { SelectDropdown } from '@shared/ui/select-dropdawn';
import { SOURCE_1CUU } from '@shared/constants/sourceData';
import { Modal, PreloaderCircular } from '@shared/ui/kit';
import { useConfirm } from '@shared/hooks/useConfirm';
import { ErrorBlock } from '@shared/ui/error-modal';
import { AddButton } from '@shared/ui/add-button';
import { useToast } from '@shared/hooks/useToast';
import { FC, useMemo, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { createPortal } from 'react-dom';
import { styles } from './styles';

interface CategoryTabProps {
  handleAdd: () => void;
  categories: Category[];
  fetching: boolean;
  refetch: () => void;
  eventId: number;
  setCategories: React.Dispatch<React.SetStateAction<Category[]>>;
  refetchSegments?: () => void;
  eventDateEnd?: string;
  eventDateStart?: string;
  refetchEvent?: () => void;
  isCBGroup?: boolean;
  eventSettings?: EventSettings | null;
}

export const CategoryTab: FC<CategoryTabProps> = ({
  handleAdd,
  categories,
  fetching,
  refetch,
  eventId,
  setCategories,
  refetchSegments,
  eventDateEnd,
  eventDateStart,
  refetchEvent,
  isCBGroup = false,
  eventSettings,
}) => {
  const [hasError, setHasError] = useState(false);
  const { openConfirmDialog } = useConfirm();

  const [selectedIds, setSelectedIds] = useState<Record<number, number[]>>({});
  const [selectedParticipants, setSelectedParticipants] = useState<number[]>([]);
  const [showModalAddParticipantToCategory, setShowModalAddParticipantToCategory] = useState(false);
  const { showToast } = useToast();
  const [, removeTickets] = useRemoveTicketsFromCategory();
  useGetParticipantsByEventId({ eventId, source: SOURCE_1CUU }, { fetchFresh: true });

  const selectedCount = useMemo(() => {
    return Object.values(selectedIds).reduce((acc, ids) => acc + ids.length, 0);
  }, [selectedIds]);

  const isDisabled = !selectedCount;
  const options = [
    { label: 'Добавить в категорию', value: 'type3', disabled: isDisabled },
    { label: 'Удалить из категории', value: 'type4', disabled: isDisabled },
  ];

  // Обрботка выбора действия в селекте
  const handleSelectChange = async (val: string) => {
    if (val === 'type3') {
      if (!selectedParticipants.length) {
        showToast('Выберите участников', 'error');
        return;
      }
      setShowModalAddParticipantToCategory(true);
    }
    if (val === 'type4') {
      await handleDeleteSelectedTickets();
      setSelectedParticipants([]);
      setSelectedIds([]);
    }
  };

  // Удаление через селект
  const handleDeleteSelectedTickets = async () => {
    for (const [categoryIdStr, ticketIds] of Object.entries(selectedIds)) {
      const categoryId = +categoryIdStr;
      await handleDeleteTickets(categoryId, ticketIds);
      setSelectedParticipants([]);
      setSelectedIds([]);
    }
  };

  // Удаления одного тикета в катеогрии по клику на корзинку
  const handleDeleteTickets = async (categoryId: number, ticketIds: number[]) => {
    if (!ticketIds.length) return;

    const category = categories.find((cat) => cat.id === categoryId);
    const title = category?.title;

    const confirmed = await openConfirmDialog(`Удалить ${ticketIds.length} участника(ов) из категории "${title}"?`);
    if (!confirmed) return;

    const { data, error } = await removeTickets({ input: { categoryId, ticketIds } });

    if (error || !data?.removeTicketsFromCategory.success) {
      setHasError(true);
      return;
    }

    setCategories((prev) =>
      prev.map((cat) =>
        cat.id === categoryId
          ? {
              ...cat,
              tickets: cat.tickets.filter((t) => !ticketIds.includes(t.id)),
              countTicket: cat.countTicket - ticketIds.length,
            }
          : cat
      )
    );

    setSelectedIds((prev) => ({
      ...prev,
      [categoryId]: (prev[categoryId] || []).filter((id) => !ticketIds.includes(id)),
    }));
  };

  return (
    <div {...stylex.props(styles.container)}>
      <div {...stylex.props(styles.actionsContainer)}>
        <AddParticipantToCategoryModal
          isOpen={showModalAddParticipantToCategory}
          onClose={() => setShowModalAddParticipantToCategory(false)}
          refetch={() => {
            refetch();
            refetchSegments?.();
            refetchEvent?.();
            setSelectedParticipants([]);
            setSelectedIds([]);
          }}
          eventId={eventId}
          isCopyParticipants
          categories={categories}
          newParticipantsIds={selectedParticipants}
        />
        <SelectDropdown
          options={options}
          value={options[0].value}
          onChange={handleSelectChange}
          selectedCount={selectedCount}
          settings={{
            height: '40px',
            padding: '8px 55px',
            fontSize: '16px',
          }}
        />
      </div>
      {fetching ? (
        <div {...stylex.props(styles.loader)}>
          <PreloaderCircular />
        </div>
      ) : !categories?.length ? (
        <div {...stylex.props(styles.containerText)}>
          <p {...stylex.props(styles.emptyText)}>В событии еще не добавлены категории</p>
        </div>
      ) : (
        <div {...stylex.props(styles.segmentsWrapper)}>
          {categories.map((category) => (
            <div key={category.id} {...stylex.props(styles.segment)}>
              <SegmentCardCategory
                setSelectedParticipants={setSelectedParticipants}
                category={category}
                handleDeleteTickets={handleDeleteTickets}
                refetch={refetch}
                eventId={eventId}
                selectedIds={selectedIds[category.id] || []}
                setSelectedIds={(newIds) => {
                  setSelectedIds((prev) => ({
                    ...prev,
                    [category.id]: newIds,
                  }));
                }}
                refetchSegments={refetchSegments}
                refetchEvent={refetchEvent}
                eventDateEnd={eventDateEnd}
                eventDateStart={eventDateStart}
                isCBGroup={isCBGroup}
                eventSettings={eventSettings}
              />
            </div>
          ))}
        </div>
      )}
      <div {...stylex.props(styles.containerBtn)}>
        <AddButton onClick={handleAdd}>Добавить категорию</AddButton>
      </div>
      {hasError &&
        createPortal(
          <Modal
            active
            settingsHeader={{ height: '16px', padding: '0px' }}
            settingsContainer={{
              height: 'auto',
              width: '480px',
              padding: '0 24px',
            }}
            settingsFooter={{ height: '16px', padding: '0px' }}
          >
            <ErrorBlock
              description="Не удалось удалить участников. Попробуйте позже."
              onClose={() => setHasError(false)}
            />
          </Modal>,
          document.getElementById('modal') as Element
        )}
    </div>
  );
};
